import { APP_VERSION } from "@/versionInfo";

/**
 * Captured once at module load — user agent, viewport, timezone don't change
 * across the lifetime of an upload session (mid-upload window resize is
 * ignored; the ratio at start is representative). Sampling on every event
 * would add cost for no diagnostic value.
 */

let cached = null;

/**
 * @returns {import('../diagnostics/UploadEvents').UploadEnvironmentSnapshot}
 */
export const getClientEnvironment = () => {
  if (cached) return cached;
  cached = build();
  return cached;
};

const build = () => {
  const ua = safeGet(() => navigator.userAgent, "");
  const { browser, os } = parseUserAgent(ua);
  return {
    browser,
    os,
    platform: safeGet(() => navigator.platform, "unknown"),
    userAgent: ua,
    language: safeGet(() => navigator.language, "unknown"),
    timezone: safeGet(
      () => Intl.DateTimeFormat().resolvedOptions().timeZone,
      "unknown"
    ),
    viewport: safeGet(
      () => ({
        width: window.innerWidth,
        height: window.innerHeight,
        pixelRatio: window.devicePixelRatio || 1,
      }),
      { width: 0, height: 0, pixelRatio: 1 }
    ),
    frontendVersion: APP_VERSION.version,
    commitSha: APP_VERSION.commitSha,
  };
};

const safeGet = (fn, fallback) => {
  try {
    const value = fn();
    return value === undefined || value === null ? fallback : value;
  } catch (_error) {
    return fallback;
  }
};

/**
 * Very small user-agent parser sufficient for admin dashboard display.
 * Avoids pulling in a 40kB `ua-parser-js`-style dependency for a feature that
 * only needs approximate labels ("Chrome 120 on macOS 14").
 */
const parseUserAgent = (ua) => {
  const browser = detectBrowser(ua);
  const os = detectOs(ua);
  return { browser, os };
};

const detectBrowser = (ua) => {
  if (!ua) return "unknown";
  if (/edg\//i.test(ua)) return `Edge ${match(ua, /edg\/([\d.]+)/i)}`;
  if (/opr\//i.test(ua)) return `Opera ${match(ua, /opr\/([\d.]+)/i)}`;
  if (/firefox\//i.test(ua)) return `Firefox ${match(ua, /firefox\/([\d.]+)/i)}`;
  if (/chrome\//i.test(ua) && !/chromium/i.test(ua)) {
    return `Chrome ${match(ua, /chrome\/([\d.]+)/i)}`;
  }
  if (/safari\//i.test(ua) && /version\//i.test(ua)) {
    return `Safari ${match(ua, /version\/([\d.]+)/i)}`;
  }
  return "unknown";
};

const detectOs = (ua) => {
  if (!ua) return "unknown";
  if (/windows nt/i.test(ua)) return `Windows ${match(ua, /windows nt ([\d.]+)/i)}`;
  if (/mac os x/i.test(ua)) {
    return `macOS ${match(ua, /mac os x ([\d_.]+)/i).replace(/_/g, ".")}`;
  }
  if (/android/i.test(ua)) return `Android ${match(ua, /android ([\d.]+)/i)}`;
  if (/iphone os/i.test(ua) || /ipad; cpu os/i.test(ua)) {
    return `iOS ${match(ua, /os ([\d_]+)/i).replace(/_/g, ".")}`;
  }
  if (/linux/i.test(ua)) return "Linux";
  return "unknown";
};

const match = (ua, regex) => {
  const m = ua.match(regex);
  return m && m[1] ? m[1] : "";
};

// Test-only cache reset.
export const __resetClientEnvironmentForTests = () => {
  cached = null;
};
